/**
 * ダッシュボード用の小さなグラフ(折れ線・棒)。外部ライブラリは使わず SVG で描く。
 * 色はブランドカラー(base.css の CSS変数)に合わせる。値は整数前提(円・件)。
 */
import type { ReactNode } from 'react';

export type Series = {
  key: string;
  label: string;
  /** 線・棒の色。CSS変数でもよい(例 'var(--azure)') */
  color: string;
  /** labels と同じ長さ。null はその日にデータが無い */
  values: (number | null)[];
};

type Props = {
  labels: string[];
  series: Series[];
  /** 'line' = 折れ線 / 'bar' = 棒(series は1本だけ使う) */
  type?: 'line' | 'bar';
  /** 値の表示(凡例と最大値の目盛り) */
  format?: (v: number) => string;
  /** x軸ラベルを何個おきに出すか(既定は自動) */
  labelEvery?: number;
  height?: number;
};

/** 目盛りの上限を切りのいい数にする */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const digits = Math.floor(Math.log10(v));
  const unit = Math.pow(10, digits);
  return Math.ceil(v / unit) * unit;
}

const W = 600;
const PAD = { top: 12, right: 10, bottom: 22, left: 46 };

export default function MiniChart({ labels, series, type = 'line', format = (v) => String(v), labelEvery, height = 170 }: Props) {
  const H = height;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const max = niceMax(Math.max(0, ...all));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const n = labels.length;
  const x = (i: number) => (n <= 1 ? PAD.left + innerW / 2 : PAD.left + (innerW * i) / (n - 1));
  const y = (v: number) => PAD.top + innerH - (innerH * v) / max;
  const every = labelEvery ?? Math.max(1, Math.ceil(n / 8));

  if (n === 0 || all.length === 0) {
    return <div className="empty">データがありません</div>;
  }

  const grid: ReactNode[] = [0, 0.5, 1].map((t) => (
    <line key={t} x1={PAD.left} x2={W - PAD.right} y1={y(max * t)} y2={y(max * t)} className="ch-grid" />
  ));

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="mini-chart" role="img" preserveAspectRatio="none">
        {grid}
        <text x={PAD.left - 6} y={y(max) + 4} className="ch-axis" textAnchor="end">
          {format(max)}
        </text>
        <text x={PAD.left - 6} y={y(0) + 4} className="ch-axis" textAnchor="end">
          0
        </text>

        {type === 'bar'
          ? (series[0]?.values ?? []).map((v, i) => {
              if (v === null) return null;
              const bw = Math.max(4, (innerW / Math.max(1, n)) * 0.6);
              return <rect key={i} x={x(i) - bw / 2} y={y(v)} width={bw} height={Math.max(0, y(0) - y(v))} fill={series[0]?.color ?? 'var(--azure)'} rx={2} />;
            })
          : series.map((s) => {
              const pts = s.values.map((v, i) => (v === null ? null : `${x(i)},${y(v)}`)).filter((p): p is string => p !== null);
              return (
                <g key={s.key}>
                  <polyline points={pts.join(' ')} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {s.values.map((v, i) => (v === null ? null : <circle key={i} cx={x(i)} cy={y(v)} r={2.5} fill={s.color} />))}
                </g>
              );
            })}

        {labels.map((l, i) =>
          i % every === 0 || i === n - 1 ? (
            <text key={l + i} x={x(i)} y={H - 6} className="ch-axis" textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}
      </svg>
      <div className="ch-legend">
        {series.map((s) => {
          const last = [...s.values].reverse().find((v): v is number => v !== null);
          return (
            <span key={s.key}>
              <i style={{ background: s.color }} />
              {s.label}
              {last !== undefined && <b>{format(last)}</b>}
            </span>
          );
        })}
      </div>
    </>
  );
}
